"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import {
  clearAuthSession,
  emailInitial,
  planLabel,
  useAccountState,
} from "./use-account-state";

/**
 * Compact account chip in the marketing navbar.
 *
 * Visible states:
 *   - loading: small skeleton dot
 *   - anonymous: ghost pill labelled "Sign in" → /login. The pill shape
 *     gives the slot consistent visual weight whether the user is in
 *     or out, so the navbar doesn't visually jump when auth state
 *     flips after first paint.
 *   - authenticated: avatar pill that opens a real dropdown menu on
 *     click. The chevron now delivers on its promise: clicking opens
 *     options instead of navigating away. Menu items cover the things
 *     a signed-in user needs from a marketing page — confirm identity,
 *     jump back to the tool, manage account, sign out, and (for free
 *     users) upgrade.
 */
export function NavAccount() {
  const state = useAccountState();

  if (state.status === "loading") {
    return (
      <span className="nav-acct nav-acct-skeleton" aria-hidden="true">
        <span className="nav-acct-dot-skel" />
      </span>
    );
  }

  if (state.status === "anonymous") {
    return (
      <Link className="nav-acct nav-acct-signin" href="/login">
        Sign in
      </Link>
    );
  }

  return <NavAccountMenu email={state.account.email} plan={state.account.plan} />;
}

function NavAccountMenu({
  email,
  plan,
}: {
  email: string;
  plan: "free" | "pro" | "agency";
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);

  // Click-outside + Escape close the menu. Roving focus into the first
  // item lets keyboard users tab around inside the menu without having
  // to leave the chip first.
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Move focus to the first menu item when the menu opens via keyboard,
  // matching the WAI-ARIA Authoring Practices menu pattern.
  useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  function handleSignOut() {
    setOpen(false);
    clearAuthSession();
    // useAccountState listens to the auth event and re-renders this
    // component into its anonymous state — no router push needed.
  }

  const label = planLabel(plan);
  const isFree = plan === "free";

  return (
    <div className="nav-acct-menu-wrap" ref={wrapperRef}>
      <button
        type="button"
        className="nav-acct nav-acct-auth"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu — ${email} on ${label} plan`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="nav-acct-avatar" aria-hidden="true">
          {emailInitial(email)}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open ? (
        <div className="nav-acct-menu" role="menu" aria-label="Account">
          <div className="nav-acct-menu-header" role="presentation">
            <p className="nav-acct-menu-email" title={email}>
              {email}
            </p>
            <span className={`nav-acct-menu-plan nav-acct-menu-plan-${plan}`}>
              {label}
            </span>
          </div>

          <div className="nav-acct-menu-divider" role="presentation" />

          <Link
            ref={firstItemRef}
            href="/dashboard"
            className="nav-acct-menu-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <MenuIcon path="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            Dashboard
          </Link>

          <Link
            href="/account"
            className="nav-acct-menu-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <MenuIcon
              path="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
              extra="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
            />
            Account &amp; billing
          </Link>

          {isFree ? (
            <Link
              href="/pricing"
              className="nav-acct-menu-item nav-acct-menu-item-upgrade"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <MenuIcon path="m12 4 3 5h-2v6h-2V9H9z" extra="M5 19h14" />
              Upgrade plan
            </Link>
          ) : null}

          <div className="nav-acct-menu-divider" role="presentation" />

          <button
            type="button"
            className="nav-acct-menu-item nav-acct-menu-item-signout"
            role="menuitem"
            onClick={handleSignOut}
          >
            <MenuIcon
              path="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
              extra="M16 17l5-5-5-5M21 12H9"
            />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`nav-acct-chevron${open ? " is-open" : ""}`}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2.75 4.5 6 7.75l3.25-3.25"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MenuIcon({ path, extra }: { path: string; extra?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="nav-acct-menu-icon"
    >
      <path d={path} />
      {extra ? <path d={extra} /> : null}
    </svg>
  );
}

export function FooterAccount() {
  const state = useAccountState();

  if (state.status === "authenticated") {
    return (
      <Link className="footer-account-link" href="/account">
        Manage account
      </Link>
    );
  }

  return (
    <Link className="footer-account-link" href="/login">
      Sign in
    </Link>
  );
}

export function FooterAccountLabel() {
  const state = useAccountState();
  if (state.status === "authenticated") return <>Account</>;
  return <>Sign in</>;
}
